import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, withOrg } from '../config';
import { notify, notifyError } from '../notify';
import { th as thStyle, td, useSort, sortRows, HeaderCell } from './sortTable';
import { Chip } from './subBits';

// Subreddits → Find subreddits: search Reddit for subs with terms typed or
// suggested from a creator's vault. The scraper runs the searches and checks
// each new sub (no AI); open, active ones land in "Found" for every org and
// creator. Karma subs we already have are listed on their own tab.

const SWITCHES = [
  { key: 'nsfw', label: 'NSFW', hint: 'NSFW subs for the terms' },
  { key: 'sfw', label: 'SFW', hint: 'SFW subs for the terms (fandom and general subs)' },
  { key: 'text', label: 'Text only', hint: 'Subs that only take text posts, plus a few story and confession searches' },
  { key: 'karma', label: 'Karma', hint: "Reddit's 500 most popular subs, for growing karma. Karma-farm subs are never added" }
];
const KIND_COLOR = { franchise: '#a78bfa', character: '#a78bfa', niche: '#ff4500', look: '#60a5fa', outfit: '#60a5fa', typed: '#aaaaaa' };
const pill = (active) => ({
  padding: '6px 14px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
  background: active ? '#ff4500' : '#2a2a2a', color: active ? '#ffffff' : '#aaaaaa'
});
const num = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString());

function TermChip({ t, onToggle, onRemove }) {
  const color = KIND_COLOR[t.kind] || '#aaa';
  return (
    <span onClick={onToggle} title={t.items ? `${t.kind}: ${t.items} vault items` : 'typed'}
      style={{ cursor: 'pointer', fontSize: '12px', padding: '3px 10px', borderRadius: '14px', border: `1px solid ${t.on ? color : '#333'}`, color: t.on ? '#fff' : '#666', background: t.on ? `${color}22` : 'transparent', display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
      {t.term}{t.items ? <span style={{ color: '#888' }}>{t.items}</span> : null}
      {t.kind === 'typed' && <span onClick={e => { e.stopPropagation(); onRemove(); }} style={{ color: '#888' }}>✕</span>}
    </span>
  );
}

function Bar({ done, total, color }) {
  const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <div style={{ height: '6px', background: '#222', borderRadius: '3px', overflow: 'hidden', margin: '4px 0' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color, transition: 'width 0.5s' }} />
    </div>
  );
}

// One run: its searches, then the checks of the new subs it found (with the
// time left at the pace so far). Checks counts need finder_progress.sql.
function RunRow({ run }) {
  const what = [run.terms.length ? `${run.terms.length} terms` : null, (run.locations || []).length ? `${run.locations.length} places` : null,
    ...Object.entries(run.switches || {}).filter(([k, v]) => v && k !== 'dating').map(([k]) => (k === 'sweep' ? 'dating everywhere' : k))].filter(Boolean).join(' · ');
  const c = run.checks;
  const total = c ? Object.values(c).reduce((t, n) => t + n, 0) : 0;
  const left = c ? c.queued || 0 : 0;
  const searching = run.done < run.searches;
  const minutes = (Date.now() - Date.parse(run.created_at)) / 60000;
  const pace = total - left > 0 && minutes > 1 ? (total - left) / minutes : 0;
  const other = c ? total - left - (c.added || 0) - (c.too_small || 0) - (c.in_db || 0) : 0;
  return (
    <div className="sub" style={{ padding: '8px 0', borderTop: '1px solid #222' }}>
      <div>{new Date(run.created_at).toLocaleString()}{run.creator_name ? ` · for ${run.creator_name}` : ''} · {what}{run.failed ? ` · ${run.failed} search(es) failed` : ''}</div>
      {searching ? <>
        <Bar done={run.done} total={run.searches} color="#f59e0b" />
        <div style={{ color: '#f59e0b' }}>Searching {run.done}/{run.searches}</div>
      </> : !c ? (
        <div>{run.queued_subs} new sub(s) sent to the check</div>
      ) : <>
        <Bar done={total - left} total={total} color={left ? '#60a5fa' : '#22c55e'} />
        <div>
          <span style={{ color: left ? '#60a5fa' : '#22c55e' }}>{left ? `Checking ${num(total - left)}/${num(total)}` : `Done · ${num(total)} checked`}</span>
          {left && pace ? ` · about ${Math.ceil(left / pace)} min left` : ''}
          {' · '}<span style={{ color: '#22c55e' }}>{num(c.added || 0)} added to Found</span>
          {` · ${num(c.too_small || 0)} too small · ${num(c.in_db || 0)} already ours${other ? ` · ${num(other)} gone / karma farm / off limits` : ''}`}
        </div>
      </>}
    </div>
  );
}

function SearchTab({ onFound, orgId }) {
  const [creators, setCreators] = useState([]);
  const [creatorId, setCreatorId] = useState('');
  const [terms, setTerms] = useState([]);
  const [input, setInput] = useState('');
  const [sw, setSw] = useState({ nsfw: true, sfw: false, text: false, karma: false });
  const [loadingTerms, setLoadingTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [runs, setRuns] = useState([]);

  const loadRuns = useCallback(() => axios.get(`${API}/api/sub-finder/runs`, withOrg(orgId)).then(r => setRuns(r.data)).catch(err => notifyError(err, 'Could not load the finder runs')), [orgId]);
  useEffect(() => {
    axios.get(`${API}/api/creators`, withOrg(orgId)).then(r => setCreators(r.data.filter(c => c.name !== 'Unassigned'))).catch(err => notifyError(err, 'Could not load the creators'));
    loadRuns();
  }, [loadRuns, orgId]);
  const running = runs.some(r => r.done < r.searches || (r.checks && r.checks.queued));
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => { loadRuns(); if (onFound) onFound(); }, 15000);
    return () => clearInterval(t);
  }, [running, loadRuns, onFound]);

  const pickCreator = async (id) => {
    setCreatorId(id);
    const typed = terms.filter(t => t.kind === 'typed');
    if (!id) { setTerms(typed); return; }
    setLoadingTerms(true);
    try {
      const { data } = await axios.get(`${API}/api/sub-finder/terms`, { params: { creator_id: id } });
      const typedKeys = new Set(typed.map(t => t.term.toLowerCase()));
      setTerms([...data.filter(t => !typedKeys.has(t.term.toLowerCase())), ...typed]);
      if (!data.length) notify('No vault content to suggest terms from — type some', 'info');
    } catch (err) { notifyError(err, 'Could not load the suggested terms'); }
    setLoadingTerms(false);
  };
  const addTyped = () => {
    const have = new Set(terms.map(t => t.term.toLowerCase()));
    const fresh = input.split(/[,\n]+/).map(t => t.trim()).filter(t => t.length >= 2 && !have.has(t.toLowerCase()));
    setTerms([...terms, ...[...new Set(fresh)].map(term => ({ term, kind: 'typed', items: 0, on: true }))]);
    setInput('');
  };
  const chosen = terms.filter(t => t.on).map(t => t.term);
  const find = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/sub-finder/runs`, { terms: chosen, switches: sw, creator_id: creatorId || undefined }, withOrg(orgId));
      notify(`${data.related ? `${data.related} related subs we have, then ` : ''}${data.searches} search(es) queued — new subs show up in Found as the scraper checks them`, 'success', 6000);
      loadRuns();
    } catch (err) { notifyError(err, 'Could not start the search'); }
    setBusy(false);
  };

  return <>
    <div className="form-row" style={{ flexWrap: 'wrap', marginBottom: '12px' }}>
      <select value={creatorId} onChange={e => pickCreator(e.target.value)} style={{ flex: 'none', minWidth: '220px' }}>
        <option value="">Suggest terms from a creator's vault…</option>
        {creators.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addTyped(); }} placeholder="Type terms, comma-separated (e.g. petite, goth, Raiden Shogun)" />
      <button className="btn-secondary" onClick={addTyped} disabled={!input.trim()}>Add</button>
    </div>
    {loadingTerms ? <p className="empty">Reading the vault…</p> : terms.length > 0 && (
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {terms.map(t => <TermChip key={t.term} t={t}
          onToggle={() => setTerms(terms.map(x => (x === t ? { ...x, on: !x.on } : x)))}
          onRemove={() => setTerms(terms.filter(x => x !== t))} />)}
      </div>
    )}
    <div className="form-row" style={{ flexWrap: 'wrap', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {SWITCHES.map(s => <button key={s.key} title={s.hint} style={pill(sw[s.key])} onClick={() => setSw({ ...sw, [s.key]: !sw[s.key] })}>{s.label}</button>)}
      </div>
      <button onClick={find} disabled={busy || (!chosen.length && !sw.karma && !sw.text)}>
        {busy ? 'Starting…' : `Find subreddits${chosen.length ? ` (${chosen.length} terms)` : ''}`}
      </button>
    </div>
    <p className="sub" style={{ margin: '10px 0' }}>First the sidebars of subs we already scraped that match the terms are read for the subs they recommend; then each term is one Reddit search. Each new sub gets a quick check (weekly visitors, rules) by the scraper — no AI cost. Open, active subs are added to Found for everyone; nothing is scraped until you choose.</p>
    {runs.length > 0 && <div style={{ marginTop: '8px' }}>{runs.map(r => <RunRow key={r.id} run={r} />)}</div>}
  </>;
}

const KARMA_COLUMNS = [
  { key: 'name', label: 'Subreddit', value: s => s.name.toLowerCase(), render: s => <>
    <a href={`https://www.reddit.com/${s.name}/`} target="_blank" rel="noreferrer" style={{ color: '#ff4500', textDecoration: 'none' }}>{s.name}</a>
    {s.is_nsfw ? <Chip color="#ef4444">NSFW</Chip> : <Chip color="#22c55e">SFW</Chip>}
    {s.submission_type === 'self' && <Chip color="#60a5fa">text only</Chip>}
  </> },
  { key: 'visitors', label: 'Weekly visitors', value: s => s.weekly_visitors_count, render: s => num(s.weekly_visitors_count) }
];

function KarmaTab() {
  const [rows, setRows] = useState(null);
  const [rating, setRating] = useState('sfw');
  const [sort, onSort] = useSort({ key: 'visitors', dir: 'desc' });
  useEffect(() => {
    axios.get(`${API}/api/sub-finder/karma-subs`).then(r => setRows(r.data)).catch(err => { notifyError(err, 'Could not load the karma subs'); setRows([]); });
  }, []);
  if (!rows) return <p className="empty">Loading…</p>;
  const shown = rows.filter(s => rating === 'all' || (rating === 'nsfw') === !!s.is_nsfw);
  return <>
    <div className="form-row" style={{ flexWrap: 'wrap', marginBottom: '10px' }}>
      {['sfw', 'nsfw', 'all'].map(r => <button key={r} style={pill(rating === r)} onClick={() => setRating(r)}>{r === 'all' ? 'All' : r.toUpperCase()} ({rows.filter(s => r === 'all' || (r === 'nsfw') === !!s.is_nsfw).length})</button>)}
    </div>
    <p className="sub" style={{ marginBottom: '10px' }}>Subs we have already scraped with 50K+ weekly visitors and nothing a fresh account trips on: no karma or account-age minimum, no verification, no bouncer bot (AutoModerator limits can't be seen). Big SFW subs grow karma fastest. Karma-farm subs are never listed.</p>
    <div style={{ overflowX: 'auto', maxHeight: '480px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead><tr>{KARMA_COLUMNS.map(c => <HeaderCell key={c.key} col={c} sort={sort} onSort={onSort} />)}</tr></thead>
        <tbody>{sortRows(shown, sort, KARMA_COLUMNS).map(s => <tr key={s.id}>{KARMA_COLUMNS.map(c => <td key={c.key} style={td}>{c.render(s)}</td>)}</tr>)}</tbody>
      </table>
      {!shown.length && <p className="empty">None yet.</p>}
    </div>
  </>;
}

// Dating: the places of US / UK / Canada / Australia with the dating subs we
// have there (and local NSFW subs named after them), rolled up city → region
// → country. Tick places to search Reddit for more (a ticked country or state
// takes in every state and city under it), or search everywhere; later
// accounts can be bound to the best places.
// locked: ticked through a place above it
function PlaceRow({ node, depth, childCount, open, onOpen, picked, locked, onPick, onShow }) {
  return (
    <tr>
      <td style={td}><input type="checkbox" checked={picked} disabled={locked} onChange={onPick} title={locked ? 'Included: a place above it is ticked' : undefined} style={{ flex: 'none' }} /></td>
      <td style={{ ...td, paddingLeft: `${8 + depth * 18}px` }}>
        {childCount > 0 ? <span onClick={onOpen} style={{ cursor: 'pointer', color: '#888', marginRight: '6px' }}>{open ? '▾' : '▸'}</span> : <span style={{ marginRight: '14px' }} />}
        <span style={{ fontWeight: depth === 0 ? 700 : 400 }}>{node.name}</span>
      </td>
      <td style={td}>{node.dating ? <span onClick={onShow} style={{ cursor: 'pointer', color: '#ff4500' }}>{node.dating}</span> : '—'}</td>
      <td style={td}>{node.local ? <span onClick={onShow} style={{ cursor: 'pointer', color: '#aaa' }}>{node.local}</span> : '—'}</td>
      <td style={td}>{node.visitors ? num(node.visitors) : '—'}</td>
    </tr>
  );
}

function DatingTab({ onFound, orgId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(new Set(['us']));
  const [picked, setPicked] = useState(new Set());
  const [shown, setShown] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/sub-finder/dating-locations`).then(r => setData(r.data)).catch(err => { notifyError(err, 'Could not load the dating map'); setData({ places: [], unplaced_dating: 0 }); });
  }, []);
  if (!data) return <p className="empty">Loading…</p>;

  const children = new Map();
  for (const p of data.places) { const k = p.parent || ''; if (!children.has(k)) children.set(k, []); children.get(k).push(p); }
  for (const list of children.values()) list.sort((a, b) => b.dating - a.dating || b.local - a.local || a.name.localeCompare(b.name));
  const flip = (set, key) => { const next = new Set(set); if (next.has(key)) next.delete(key); else next.add(key); return next; };
  const byKey = new Map(data.places.map(p => [p.key, p]));
  const underPicked = (node) => {
    for (let p = byKey.get(node.parent); p; p = byKey.get(p.parent)) if (picked.has(p.key)) return true;
    return false;
  };
  // What a search covers: the ticked states and cities and all under a
  // ticked place (a country is searched through them)
  const covered = data.places.filter(p => p.level !== 'country' && (picked.has(p.key) || underPicked(p)));
  const rows = [];
  const walk = (parent, depth) => {
    for (const node of children.get(parent) || []) {
      const kids = (children.get(node.key) || []).length;
      rows.push(<PlaceRow key={node.key} node={node} depth={depth} childCount={kids} open={open.has(node.key)}
        onOpen={() => setOpen(flip(open, node.key))} picked={picked.has(node.key) || underPicked(node)} locked={underPicked(node)}
        onPick={() => setPicked(flip(picked, node.key))} onShow={() => setShown(shown && shown.key === node.key ? null : node)} />);
      if (open.has(node.key)) walk(node.key, depth + 1);
    }
  };
  walk('', 0);
  const start = async (body, where) => {
    setBusy(true);
    try {
      const { data: run } = await axios.post(`${API}/api/sub-finder/runs`, body, withOrg(orgId));
      notify(`${run.related ? `${run.related} related dating subs we have, then ` : ''}${run.searches} dating search(es) queued ${where} — progress on the Find tab; new subs show up in Found`, 'success', 6000);
      setPicked(new Set());
      if (onFound) onFound();
    } catch (err) { notifyError(err, 'Could not start the dating search'); }
    setBusy(false);
  };
  const searchPlaces = () => start({ switches: { dating: true }, locations: covered.map(p => p.key) }, `for ${covered.length} place(s)`);
  const searchEverywhere = () => start({ switches: { dating: true, sweep: true }, locations: [] }, 'across Reddit');
  const searches = covered.length * data.place_words.length;

  return <>
    <div className="form-row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: '10px' }}>
      <span className="sub">
        Dating subs we have per place, and local NSFW subs named after it. {data.unplaced_dating} dating subs name no place (r/r4r-style or outside these countries). Click a count to see the subs.
        {' '}A ticked country or state takes in every state and city under it, each searched {data.place_words.length} ways ({data.place_words.join(', ')}). Search everywhere looks for {data.sweep_words.join(', ')} subs across Reddit, several pages each. Every dating search first reads the sidebars of the place's dating subs we already have (everywhere: all of them) for the subs they recommend.
      </span>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        <button className="btn-secondary btn-small" disabled={busy} onClick={searchEverywhere}>Search everywhere</button>
        <button className="btn-secondary btn-small" disabled={!picked.size} onClick={() => setPicked(new Set())}>Clear</button>
        <button className="btn-small" disabled={busy || !covered.length} onClick={searchPlaces}>
          {covered.length ? `Search ${covered.length} place${covered.length === 1 ? '' : 's'} · ${searches} searches` : 'Search places'}
        </button>
      </div>
    </div>
    <div style={{ overflowX: 'auto', maxHeight: '480px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead><tr>{['', 'Place', 'Dating subs', 'Local NSFW', 'Weekly visitors'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
    {shown && (
      <div style={{ marginTop: '12px' }}>
        <p className="sub" style={{ marginBottom: '6px' }}>{shown.name}: {shown.subs.length} sub(s)</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {[...shown.subs].sort((a, b) => (b.weekly_visitors || 0) - (a.weekly_visitors || 0)).map(s => (
            <a key={s.id} href={`https://www.reddit.com/${s.name}/`} target="_blank" rel="noreferrer" className="tag" style={{ textDecoration: 'none', color: s.kind === 'dating' ? '#ff4500' : '#aaa' }}
              title={`${s.kind === 'dating' ? 'dating' : 'local NSFW'} · ${s.places.join(', ')}${s.scrape_status === 'discovered' ? ' · found, not scraped' : ''}`}>
              {s.name} {s.weekly_visitors ? `· ${num(s.weekly_visitors)}` : ''}
            </a>
          ))}
        </div>
      </div>
    )}
  </>;
}

export default function SubFinder({ onClose, onFound, viewingOrgId }) {
  const [tab, setTab] = useState('search');
  return (
    <div className="card">
      <div className="form-row" style={{ justifyContent: 'space-between', marginBottom: '12px' }}>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button style={pill(tab === 'search')} onClick={() => setTab('search')}>🔎 Find subreddits</button>
          <button style={pill(tab === 'karma')} onClick={() => setTab('karma')}>Karma subs we have</button>
          <button style={pill(tab === 'dating')} onClick={() => setTab('dating')}>Dating locations</button>
        </div>
        <button className="btn-secondary btn-small" onClick={onClose}>Close</button>
      </div>
      {/* keyed by org: switching orgs clears the picked creator and its terms */}
      {tab === 'search' ? <SearchTab key={viewingOrgId || 'own'} orgId={viewingOrgId} onFound={onFound} /> : tab === 'karma' ? <KarmaTab /> : <DatingTab orgId={viewingOrgId} onFound={onFound} />}
    </div>
  );
}
